import { Component } from '@theme/component';
import { QuantitySelectorUpdateEvent } from '@theme/events';

/**
 * A custom element that allows the user to select a quantity.
 *
 * @typedef {Object} Refs
 * @property {HTMLInputElement} quantityInput
 *
 * @extends {Component<Refs>}
 */
class QuantitySelectorComponent extends Component {
  /**
   * Cart qty must not change while rental-collateral sync is settling (double-RC race).
   * PDP quantity (no data-cart-line) stays interactive.
   */
  #isCartQtyLocked() {
    if (!this.refs.quantityInput?.dataset?.cartLine) return false;

    // Digital / DDF product qty must stay editable while RC sync locks other lines.
    const row = this.closest('tr.cart-items__table-row');
    if (row instanceof HTMLElement && (row.dataset.isDigital === 'true' || row.dataset.digitalFee === 'true')) {
      return false;
    }

    return (
      document.documentElement.classList.contains('rc-payments-pending') ||
      Boolean(this.closest('cart-items-component')?.classList.contains('rc-qty-locked'))
    );
  }

  /**
   * Handles the quantity increase event.
   * @param {Event} event - The event.
   */
  increaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (this.#isCartQtyLocked()) {
      event.preventDefault();
      return;
    }

    event.preventDefault();
    const { quantityInput } = this;
    const previousValue = quantityInput.value;
    quantityInput.stepUp();
    // Clamped at min/max leaves the value untouched; skip the no-op cart request.
    if (quantityInput.value === previousValue) return;
    this.#onQuantityChange();
  }

  /**
   * Handles the quantity decrease event.
   * @param {Event} event - The event.
   */
  decreaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (this.#isCartQtyLocked()) {
      event.preventDefault();
      return;
    }

    event.preventDefault();
    const { quantityInput } = this;
    const previousValue = quantityInput.value;
    quantityInput.stepDown();
    // Clamped at min/max leaves the value untouched; skip the no-op cart request.
    if (quantityInput.value === previousValue) return;
    this.#onQuantityChange();
  }

  /**
   * When our input gets focused, we want to fully select the value.
   * @param {FocusEvent} event
   */
  selectInputValue(event) {
    const { quantityInput } = this.refs;
    if (!(event.target instanceof HTMLInputElement) || document.activeElement !== quantityInput) return;

    quantityInput.select();
  }

  /**
   * Handles the quantity set event.
   * @param {Event} event - The event.
   */
  setQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (this.#isCartQtyLocked()) {
      event.preventDefault();
      return;
    }

    event.preventDefault();
    if (event.target instanceof HTMLInputElement) {
      this.refs.quantityInput.value = event.target.value;
    }
    this.#onQuantityChange();
  }

  /**
   * Handles the quantity change event.
   */
  #onQuantityChange() {
    const { quantityInput } = this.refs;

    this.#checkQuantityRules();
    const newValue = parseInt(quantityInput.value);

    quantityInput.dispatchEvent(new QuantitySelectorUpdateEvent(newValue, Number(quantityInput.dataset.cartLine)));
  }

  /**
   * Checks the quantity rules are met
   */
  #checkQuantityRules = () => {
    const { quantityInput } = this.refs;
    const { min, max } = quantityInput;
    const fallback = min !== '' ? min : '1';
    const newValue = Number(quantityInput.value);

    // A cleared or non-numeric field reads as '' / NaN; normalize it back to the
    // floor so the cart still receives an actionable quantity (0 removes the line).
    if (quantityInput.value.trim() === '' || Number.isNaN(newValue)) {
      quantityInput.value = fallback;
      return;
    }

    if (min !== '' && newValue < Number(min)) quantityInput.value = min;
    if (max !== '' && newValue > Number(max)) quantityInput.value = max;
  };

  /**
   * Gets the quantity input.
   * @returns {HTMLInputElement} The quantity input.
   */
  get quantityInput() {
    if (!this.refs.quantityInput) {
      throw new Error('Missing <input ref="quantityInput" /> inside <quantity-selector-component />');
    }

    return this.refs.quantityInput;
  }
}

if (!customElements.get('quantity-selector-component')) {
  customElements.define('quantity-selector-component', QuantitySelectorComponent);
}
